// App.js
/* global ShopifyAnalytics */
// Trigger rebuild for backend URL injection

// ========================================
// 🔗 Imports & Setup
// ========================================

import React, { useState, useEffect, useCallback, } from "react";
import { Web3Provider } from "@ethersproject/providers";
import Cookies from "js-cookie";
import axios from "axios";
import { PowerIcon } from "@heroicons/react/24/solid";
import "./App.css";
import { useConnectWallet } from "@web3-onboard/react";
import { useWallet, WalletProvider } from "./walletContext";

// ========================================
// 🔐 Countdown Utility UI
// ========================================

const getCountdownString = (unlockDate) => {
  const ms = new Date(unlockDate) - new Date();
  if (ms <= 0) return "now";

  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);

  return `${days > 0 ? `${days}d ` : ""}${hours}h ${minutes}m`;
};

// ========================================
// 💳 WalletConnectWithDiscount Component
// ========================================

const WalletConnectWithDiscount = () => {
  const {
    wallet,
    setWallet,
    setSignature,
    setNonce,
    authenticateWallet,
    discountPercentage,
    setDiscountPercentage,
  } = useWallet();

  const [{ wallet: onboardWallet, connecting }, connect, disconnect] = useConnectWallet();
  const [error, setError] = useState(null);
  const [storageSafe, setStorageSafe] = useState(true);

  // ✅ Check if localStorage access is safe (not inside sandboxed iframe)
  useEffect(() => {
    try {
      const test = "__storage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
    } catch (err) {
      console.warn("🚫 LocalStorage is not accessible:", err);
      setStorageSafe(false);
    }
  }, []);

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const connectWallet = useCallback(async () => {
    try {
      setError(null);
      await connect();
    } catch (err) {
      console.error("Wallet connection failed:", err);
      setError("Failed to connect wallet. Please try again or install a wallet like MetaMask.");
    }
  }, [connect]);

  const disconnectWallet = useCallback(() => {
    if (onboardWallet) {
      disconnect({ label: onboardWallet.label });
    }
    if (storageSafe) {
      localStorage.removeItem("wallet_address");
      localStorage.removeItem("wallet_connected");
      localStorage.removeItem("wallet_chain_id");
    }
    Cookies.remove("wallet_discount_code");
    Cookies.remove("wallet_discount_percentage");
    setWallet("");
    setSignature("");
    setNonce("");
    setDiscountPercentage(null);
    setError(null);
    console.log("🔌 Wallet disconnected");
  }, [onboardWallet, disconnect, storageSafe, setWallet, setSignature, setNonce, setDiscountPercentage]);

  useEffect(() => {
    const code = Cookies.get("wallet_discount_code");
    const storeDomain = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
    if (!storeDomain) {
      console.error("REACT_APP_SHOPIFY_STORE_DOMAIN is not defined");
      setError("Shopify store domain not configured");
      return;
    }
    if (!code) return;

    const hasRedirected = Cookies.get("wallet_discount_redirected");
    if (hasRedirected === "true") return;

    const currentURL = window.location.href;
    const redirectTo = encodeURIComponent(currentURL);
    Cookies.set("wallet_discount_redirected", "true", { path: "/", expires: 0.02 });
    window.location.href = `https://${storeDomain}/discount/${code}?redirect=${redirectTo}`;
  }, []);

  useEffect(() => {
    if (!onboardWallet?.provider) return;

    const handleAccountsChanged = async (accounts) => {
      if (accounts.length > 0) {
        const ethersProvider = new Web3Provider(onboardWallet.provider);
        const newAddress = accounts[0].toLowerCase();
        setWallet(newAddress);
        if (storageSafe) {
          localStorage.setItem("wallet_address", newAddress);
        }
        try {
          await authenticateWallet(newAddress, ethersProvider);
          await createDiscount(newAddress);
        } catch (err) {
          setError("Account change failed: " + err.message);
        }
      } else {
        disconnectWallet();
      }
    };

    const createDiscount = debounce(async (address) => {
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/create-discount`,
          { address },
          { timeout: 5000 }
        );

        if (data?.discountCode && data?.discountPercentage) {
          Cookies.set("wallet_discount_code", data.discountCode, { path: "/", expires: 7 });
          Cookies.set("wallet_discount_percentage", data.discountPercentage, {
            path: "/",
            expires: 7,
          });
          setDiscountPercentage(data.discountPercentage);
        } else {
          Cookies.remove("wallet_discount_code");
          Cookies.remove("wallet_discount_percentage");
          setDiscountPercentage(null);
        }
      } catch (err) {
        console.error("Failed to create discount:", err);
        Cookies.remove("wallet_discount_code");
        Cookies.remove("wallet_discount_percentage");
        setDiscountPercentage(null);
        setError("Failed to create discount: " + err.message);
      }
    }, 1000);

    const setupProvider = async () => {
      try {
        const ethersProvider = new Web3Provider(onboardWallet.provider);
        const network = await ethersProvider.getNetwork();
        const expectedChainId = "0x1";
        if (network.chainId !== parseInt(expectedChainId, 16)) {
          await onboardWallet.provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: expectedChainId }],
          });
        }

        const address = await ethersProvider.getSigner().getAddress();
        const normalized = address.toLowerCase();
        setWallet(normalized);
        if (storageSafe) {
          localStorage.setItem("wallet_address", normalized);
          localStorage.setItem("wallet_connected", "web3-onboard");
        }
        await authenticateWallet(normalized, ethersProvider);
        await createDiscount(normalized);
      } catch (err) {
        console.error("Setup provider failed:", err);
        setError("Wallet setup failed: " + err.message);
      }
    };

    setupProvider();
    onboardWallet.provider.on("accountsChanged", handleAccountsChanged);
    return () => {
      onboardWallet.provider.removeListener("accountsChanged", handleAccountsChanged);
    };
  }, [onboardWallet, authenticateWallet, setWallet, setDiscountPercentage, disconnectWallet, storageSafe]);

  useEffect(() => {
    if (!storageSafe) return;
    const cached = localStorage.getItem("wallet_connected");
    if (cached === "web3-onboard" && !onboardWallet) {
      console.log("Auto-connecting wallet from cache");
      connectWallet();
    }
  }, [connectWallet, onboardWallet, storageSafe]);

  return (
    <div className="wallet-container">
      {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
      <button
        onClick={() => (onboardWallet ? disconnectWallet() : connectWallet())}
        className={`wallet-button px-4 py-2 rounded flex items-center gap-2 ${
          connecting ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 text-white"
        }`}
        disabled={connecting}
      >
        {connecting ? (
          <span>Connecting...</span>
        ) : onboardWallet ? (
          <div className="wallet-connected-content flex items-center gap-2">
            <div className="wallet-info-inline flex flex-col sm:flex-row sm:gap-2">
              <span className="wallet-address">
                {wallet ? `${wallet.slice(0, 6)}...${wallet.slice(-4)}` : "Connected"}
              </span>
              {discountPercentage && (
                <span className="wallet-discount-text">{discountPercentage}% OFF</span>
              )}
            </div>
            <span className="wallet-disconnect-icon">
              <PowerIcon height={20} width={20} />
            </span>
          </div>
        ) : (
          <span className="wallet-connect-label">Connect Wallet</span>
        )}
      </button>
    </div>
  );
};

// ========================================
// 🛍️ Shopify Product ID Utility
// ========================================

const getShopifyProductId = () => {
  try {
    if (ShopifyAnalytics?.meta?.product?.id) {
      return ShopifyAnalytics.meta.product.id.toString();
    }
    console.warn("ShopifyAnalytics not available, falling back to schema");
    const json = document.querySelector('[type="application/ld+json"]');
    if (json) {
      const data = JSON.parse(json.textContent);
      if (data && data['@type'] === 'Product' && data.sku) {
        return data.sku.toString();
      }
    }
    console.warn("No product ID found in schema");
    return null;
  } catch (err) {
    console.error("Failed to get Shopify product ID:", err);
    return null;
  }
};

const getShopifyVariantId = () => {
  try {
    const input = document.querySelector('form[action="/cart/add"] input[name="id"]');
    return input?.value?.toString() || null;
  } catch (err) {
    console.error("Failed to get Shopify variant ID:", err);
    return null;
  }
};

// ========================================
// 🔢 Tier Names
// ========================================

const tierNames = {
  1: "Tier 1 (100,000–249,999 tokens)",
  2: "Tier 2 (250,000–499,999 tokens)",
  3: "Tier 3 (500,000–999,999 tokens)",
  4: "Tier 4 (1,000,000+ tokens)",
};

// ========================================
// 🔐 useProductAccess Hook
// ========================================

export const useProductAccess = (productId, wallet, signature, nonce) => {
  const [state, setState] = useState({
    loading: true,
    hasAccess: null,
    tier: null,
    error: null,
  });

  useEffect(() => {
    const checkAccess = async () => {
      if (!process.env.REACT_APP_BACKEND_URL) {
        console.error("REACT_APP_BACKEND_URL is not defined");
        setState({
          hasAccess: false,
          tier: null,
          error: "Backend URL not configured",
          loading: false,
        });
        return;
      }

      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/check-access/${productId}`, {
          params: { wallet, signature, nonce },
        });

        setState({
          hasAccess: res.data.access,
          tier: res.data.tier ?? null,
          error: null,
          loading: false,
        });
      } catch (err) {
        console.error("Access check failed:", err);
        setState({
          hasAccess: false,
          tier: null,
          error: "Access check failed",
          loading: false,
        });
      }
    };

    if (wallet && signature && nonce && productId) {
      checkAccess();
    } else {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [wallet, signature, nonce, productId]);

  return state;
};

// ========================================
// 🧩 GatedAccessButton (CTA Only)
// ========================================

export const GatedAccessButton = () => {
  const { wallet, signature, nonce } = useWallet();
  const [productId, setProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tier, setTier] = useState(null);
  const [access, setAccess] = useState(null);
  const [unlockDate, setUnlockDate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const id = getShopifyProductId();
      if (id) setProductId(id);
    } catch (err) {
      console.warn("Failed to get product ID:", err);
    }
  }, []);

  useEffect(() => {
    const fetchAccess = async () => {
      if (!wallet || !signature || !nonce || !productId) return;
      if (!process.env.REACT_APP_BACKEND_URL) {
        console.error("REACT_APP_BACKEND_URL is not defined");
        setError("Backend URL not configured");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/check-access/${productId}`, {
          params: { wallet, signature, nonce },
        });

        const { access, tier } = res.data;
        setAccess(access);
        setTier(tier);

        if (!access) {
          const schedule = (await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/product-unlock/${productId}`)).data;
          if (schedule?.tiers?.[tier]) {
            setUnlockDate(new Date(schedule.tiers[tier]));
          } else if (schedule?.public) {
            setUnlockDate(new Date(schedule.public));
          }
        }

        setError(null);
      } catch (err) {
        console.error("Access check failed:", err);
        setError("Could not determine access.");
      } finally {
        setLoading(false);
      }
    };

    fetchAccess();
  }, [wallet, signature, nonce, productId, setTier]);

  if (!productId) return <button disabled>Loading product...</button>;
  if (!wallet) return <button disabled>Connect Wallet to View Access</button>;
  if (loading) return <button disabled>Checking Access...</button>;
  if (error) return <button disabled>{error}</button>;

  if (access) {
    return <button className="buy-now">✅ Available for Purchase</button>;
  }

  return (
    <button disabled>
      ⏳ Your tier: {tierNames[tier] || 'Unknown'} — Unlocks in {getCountdownString(unlockDate)}
    </button>
  );
};

// ========================================
// 📦 Open Cart Drawer Utility
// ========================================

const openCartDrawer = () => {
  if (window.Shopify && typeof window.Shopify.onCartUpdate === "function") {
    fetch("/cart.js")
      .then((res) => res.json())
      .then((cart) => {
        const event = new CustomEvent("cart:updated", { detail: { cart } });
        document.dispatchEvent(event);
      });
  } else {
    console.warn("🛑 Shopify cart drawer event not available.");
  }
};

// ========================================
// 🛒 WalletGatedCartButton
// ========================================

export const WalletGatedCartButton = () => {
  const { wallet, signature, nonce } = useWallet();

  const [productState, setProductState] = useState({
    access: null,
    tier: null,
    unlockDate: null,
    isGated: false,
    loading: true,
    error: null,
    productId: null,
    variantId: null,
  });

  const { productId, access, tier, unlockDate, loading, error, variantId } = productState;

  useEffect(() => {
    const productId = getShopifyProductId();
    const variantId = getShopifyVariantId();

    if (!productId || !variantId) {
      console.warn("⚠️ Product or variant ID not found — gating may break.");
      setProductState((prev) => ({
        ...prev,
        error: "Product or variant ID not found",
        loading: false,
      }));
      return;
    }

    setProductState((prev) => ({
      ...prev,
      productId,
      variantId,
    }));
  }, []);

  useEffect(() => {
    if (!wallet || !signature || !nonce || !productId) {
      console.log("🚫 Missing required param:", { wallet, signature, nonce, productId });
      setProductState((prev) => ({ ...prev, access: false, loading: false }));
      return;
    }

    const checkAccess = async () => {
      if (!process.env.REACT_APP_BACKEND_URL) {
        console.error("REACT_APP_BACKEND_URL is not defined");
        setProductState((prev) => ({
          ...prev,
          access: false,
          error: "Backend URL not configured",
          loading: false,
        }));
        return;
      }

      console.log("🚀 Running access check");
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/check-access/${productId}`, {
          params: { wallet, signature, nonce },
        });

        const { access, tier } = res.data;
        let unlockDate = null;
        console.log("🧪 Access check result:", { access, tier });

        if (!access) {
          const scheduleRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/product-unlock/${productId}`);
          const schedule = scheduleRes.data;

          if (schedule?.tiers?.[tier]) {
            unlockDate = new Date(schedule.tiers[tier]);
          } else if (schedule?.public) {
            unlockDate = new Date(schedule.public);
          }
        }
        console.log("🕓 Unlock date determined:", unlockDate);

        setProductState((prev) => ({
          ...prev,
          access,
          tier,
          unlockDate,
          isGated: typeof tier === "number",
          loading: false,
          error: null,
        }));
      } catch (err) {
        console.error("Access check failed:", err);
        setProductState((prev) => ({
          ...prev,
          access: false,
          error: "Token access check failed",
          loading: false,
        }));
      }
    };

    checkAccess();
  }, [wallet, signature, nonce, productId]);

  useEffect(() => {
    const maybeToggleAddToCart = () => {
      const nativeBtn = document.getElementById("native-add-to-cart");
      if (!nativeBtn) return;
      nativeBtn.style.display = productState.isGated && !productState.access ? "none" : "block";
    };

    maybeToggleAddToCart();

    const observer = new MutationObserver(maybeToggleAddToCart);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [productState.access, productState.isGated]);

  const triggerCartDrawer = () => {
    const cartButton = document.querySelector('[aria-controls="CartDrawer"]');
    if (cartButton) {
      cartButton.click();
    } else {
      console.warn("Cart drawer toggle button not found, falling back to openCartDrawer");
      openCartDrawer();
    }
  };

  const handleAddToCart = async () => {
    if (!variantId) {
      console.error("No variant ID available for adding to cart");
      return;
    }

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      const data = await res.json();
      console.log("🛒 Product added:", data);

      triggerCartDrawer();
      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } catch (err) {
      console.error("❌ Failed to add product to cart:", err);
    }
  };

  if (!productId) {
    return <button className="button w-full bg-primary text-contrast py-3 px-6" disabled>Loading product...</button>;
  }

  if (loading) {
    return <button className="button w-full bg-primary text-contrast py-3 px-6" disabled>Checking token access...</button>;
  }

  if (!wallet) {
    return <button className="button w-full bg-primary text-contrast py-3 px-6" disabled>🔒 Connect Wallet to Unlock</button>;
  }

  if (error) {
    return <button className="button w-full bg-primary text-contrast py-3 px-6" disabled>{error}</button>;
  }

  if (!access) {
    if (tier === 0 || tier === null) {
      return <button className="button w-full bg-primary text-contrast py-3 px-6" disabled>🔒 Not enough tokens for early access</button>;
    }
    return <button className="button w-full bg-primary text-contrast py-3 px-6" disabled>⏳ Your tier: {tierNames[tier] || 'Unknown'} — Unlocks in {getCountdownString(unlockDate)}</button>;
  }

  return (
    <button
      onClick={handleAddToCart}
      style={{
        backgroundColor: 'black',
        color: 'white',
        padding: '12px 24px',
        borderRadius: '6px',
        fontWeight: '600',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginTop: '16px',
        cursor: 'pointer',
        width: '100%',
        display: 'block',
      }}
    >
      Add to Cart
    </button>
  );
};

// ========================================
// 🛡️ ProductGate Wrapper
// ========================================
export const ProductGate = ({ children }) => {
  const { wallet, signature, nonce } = useWallet();
  const [productId, setProductId] = useState(null);

  useEffect(() => {
    const id = getShopifyProductId();
    if (id) setProductId(id);
  }, []);

  const { loading, hasAccess, tier, error } = useProductAccess(productId, wallet, signature, nonce);

  if (!productId || loading) {
    return (
      <button className="button w-full bg-primary text-contrast font-medium py-3 px-6 rounded shadow text-base" disabled>
        {loading ? "Checking access..." : "Loading product..."}
      </button>
    );
  }

  if (error) {
    return (
      <button className="button w-full bg-primary text-contrast font-medium py-3 px-6 rounded shadow text-base" disabled>
        ❌ {error}
      </button>
    );
  }

  if (!hasAccess) {
    return (
      <button className="button w-full bg-primary text-contrast font-medium py-3 px-6 rounded shadow text-base" disabled>
        {tier === 0 || tier === null
          ? "🔒 Not enough tokens for early access"
          : `⏳ Your tier: ${tierNames[tier] || 'Unknown'} — Unlocks soon`}
      </button>
    );
  }

  return <>{children}</>;
};

// ========================================
// 🏠 App Component
// ========================================
const App = () => {
  return (
    <WalletProvider>
      <WalletConnectWithDiscount />
    </WalletProvider>
  );
};

export default App;

  
  
